import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Token, UserDetails } from 'src/app/app.model';
import { AppService } from 'src/app/app.service';
import { CacheService } from 'src/app/cache.service';
import { StepsBuilderHelperService } from 'src/app/connector-studio/services/steps-builder-helper/steps-builder-helper.service';
import { NS_Enums } from '../../namespaces';
import {  get as _get, isUndefined as _isUndefined } from 'lodash-es';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { stepCreationButtons, stepCreationFields } from 'src/app/connector-studio/utilities/step-graph-data';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Step } from 'src/app/connector-studio/interfaces/stepGraph';
import { CommonDialogComponent } from 'src/app/common-dialog/common-dialog.component';

@Component({
  selector: 'app-step-creation-header',
  templateUrl: './step-creation-header.component.html',
  styleUrls: ['./step-creation-header.component.scss']
})
export class StepCreationHeaderComponent implements OnInit {
  loggedIn: boolean;
  userDetails: UserDetails;
  userId: number;
  currentStage: any;
  currentStep: Step;
  dataSourceType: string;
  
  private unsubscribe$ = new Subject<void>();

  constructor(private cacheService: CacheService, private appService: AppService, 
    public stepsBuilderHelperService: StepsBuilderHelperService, 
    private route: ActivatedRoute, private router: Router, 
    public dialog: MatDialog, private snackBar: MatSnackBar ) { }

  ngOnInit(): void {
    const saras_auth_key = this.cacheService.getObject<Token>(NS_Enums.cacheKeysEnum.saras_auth_key);
    if (saras_auth_key) {
      this.loggedIn = true;
      const user_details = this.cacheService.getObject<any>(NS_Enums.cacheKeysEnum.user_details);
      if (!user_details) {
        this.appService.getUser().then((user) => {
          this.userDetails = user;
          this.cacheService.putObject(NS_Enums.cacheKeysEnum.user_details, user, true);
          this.userId = user.userId;
        });
      } else if(user_details.role !== 'OrganizationAdmin') {
        this.userDetails = user_details;
        this.userId = user_details.userId;
      } else {
        this.userId = user_details.userId;
        this.userDetails = user_details
      }
    }
    this.appService.initializeService();
    this.getRouteMetaData();
    this.routerListener();

    this.stepsBuilderHelperService.stepChangeEvent$.pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe({
      next: ({event, currentStep}) => {
        this.currentStep = currentStep;
      }
    })

    this.stepsBuilderHelperService.stepGraphs$.pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe({
      next: (value) => {
        this.currentStep = this.stepsBuilderHelperService.getCurrentStepNode()
        console.log(this.currentStep)
      }
    })
  }

  routerListener() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.unsubscribe$)
      )
    .subscribe((event) => {
      this.getRouteMetaData()
    });
  }

  getRouteMetaData() {
    const child = this.route.firstChild;
    this.dataSourceType = child.snapshot.paramMap.get('dataSourceType') || this.stepsBuilderHelperService.studioSourceType;
    if(child) {
      child.data.subscribe({
        next: (data) => {
          this.currentStage = _get(data, 'stage')
        }
      })
    }
  }


  // createNewStep() {
  //   this.stepsBuilderHelperService.createNewStep('NORMAL_STEP_NODE');
  // }

  async createNewStep(isFirstTable ? : Boolean) {
    // Loaded on demand so the connector-studio dialog (and jsoneditor) stays out of the eager bundle
    const { StudioCommonDialogComponent } = await import('src/app/connector-studio/components/studio-common-dialog/studio-common-dialog.component');
    const dialogRef = this.dialog.open(StudioCommonDialogComponent, {
      panelClass: ['step-details'],
      data: {
        title: 'Create new step',
        buttons: stepCreationButtons,
        fields: stepCreationFields
      }
    });

    dialogRef.afterClosed()
      .subscribe({
        next: ({
          data,
          response
        } = {}) => {
          if (_isUndefined(response) || _get(response, 'type') === 'no-change') {
            return
          }
          const stepTitle = _get(data, 'formValue.stepTitle')
          const nodeType = _get(data, 'formValue.nodeType')
          this.stepsBuilderHelperService.isStepBeingCreated = true;

          const createStepEvent = this.stepsBuilderHelperService.getCreateNodeEvent({
            title: stepTitle,
            nodeType: nodeType
          })

          const createStep$ = this.stepsBuilderHelperService.createNewStep(createStepEvent);
          createStep$.subscribe({
            next: (value) => {
              this.stepsBuilderHelperService.isStepBeingCreated = false;
              this.snackBar.open(
                'Step created successfully', '', {
                  duration: 2500,
                  verticalPosition: 'top',
                  horizontalPosition: 'center',
                  panelClass: ['warning-snack-bar']
                });
                this.stepsBuilderHelperService.addStepGraph(createStepEvent)
            },
            error: (err) => {
              const errorMessage = _get(err, 'error.message')
              this.stepsBuilderHelperService.isStepBeingCreated = false;
              this.snackBar.open(
                errorMessage || 'Unable to add new step', '', {
                  duration: 2500,
                  verticalPosition: 'top',
                  horizontalPosition: 'center',
                  panelClass: ['warning-snack-bar']
                });
            },
            complete: () => {
              console.log('completed')
            }
          })

        },
        error: (error) => {
          console.log(error)
        }
      })
  }

  emitSaveEvent(type) {
    this.stepsBuilderHelperService.saveEventListener(type);
  }

  deleteStep() {
    const dialogRef = this.dialog.open(CommonDialogComponent, {
      data: {
        title: 'Delete step',
        steps: true, 
        content: 'Are you sure you want to delete this step?',
        image: 'warning',
        buttons: [
          {
            type: 'delete',
            title: 'Delete',
          },
          {
            type: 'cancel',
            title: 'Cancel',
          }
        ]
      },
      disableClose: true
    })

    dialogRef.afterClosed().subscribe({
      next: (response) => {
        if(_get(response, 'type') === 'delete') {
          this.stepsBuilderHelperService.isStepBeingDeleted = true;
          const currentStep = this.stepsBuilderHelperService.getCurrentStepNode()
          if(currentStep.nodeType === 'FIRST_STEP_NODE') {
            return
          }
          const deleteNodeEvent = this.stepsBuilderHelperService.getDeleteNodeEvent(currentStep)
          const deleteStep$ = this.stepsBuilderHelperService.deleteStep(deleteNodeEvent)
          deleteStep$.subscribe({
            next: (value) => {
              this.openSnackBar('Step deleted successfully', '');
              this.stepsBuilderHelperService.isStepBeingDeleted = false;
              this.stepsBuilderHelperService.deleteStepGraph(deleteNodeEvent)
            },
            error: (err) => {
              const errorMessage = _get(err, 'error.message')
              this.stepsBuilderHelperService.isStepBeingDeleted = false;
              this.openSnackBar(errorMessage || 'Unable to delete step', '');
            }
          })
        } else if(_get(response, 'type') === 'cancel') {
          return
        }
      }
    })
    
  }

  builderHeaderClickHandler(event) {
    event.stopPropagation();
    this.stepsBuilderHelperService.builderClickHandler(event)
  }

  openSnackBar(message, fallbackMessage) {
    this.snackBar.open(
      message || fallbackMessage, '',
      {duration: 2500, verticalPosition: 'top',horizontalPosition: 'center', panelClass: ['warning-snack-bar']});
  }

  ngOnDestroy(): void {
    if (this.unsubscribe$) {
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
    }
    // this.studioIntegrationService.invokeRowSelectEvent.unsubscribe();
  }

}
